import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';

const DATA = '/blog/comma-compression/data/layers';
const SAMPLE_PAIR = 175;

interface LayerSpec {
  index: number;
  h: number;
  w: number;
  channels: number;
}

const STAGES_06: LayerSpec[] = [
  { index: 0, h: 6, w: 8, channels: 36 },
  { index: 1, h: 12, w: 16, channels: 36 },
  { index: 2, h: 24, w: 32, channels: 36 },
  { index: 3, h: 48, w: 64, channels: 27 },
  { index: 4, h: 96, w: 128, channels: 20 },
  { index: 5, h: 192, w: 256, channels: 18 },
];

const FINAL_STAGE: LayerSpec = { index: 6, h: 384, w: 512, channels: 18 };

const Wrapper = styled.div`
  margin: 2.5rem 0;
  padding: 1.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg-alt);
`;

const Heading = styled.div`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 1rem;
`;

const StagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.85rem;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FullWidthGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.85rem;
`;

const PairGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const CardMotion = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.55rem;
  border: 1px solid transparent;
  border-radius: 4px;
  transition: border-color 0.2s ease, transform 0.2s ease, background 0.2s ease;
  cursor: default;

  &:hover {
    border-color: var(--accent);
    background: var(--bg);
  }
`;

const ImgFrame = styled.div`
  width: 100%;
  border-radius: 3px;
  overflow: hidden;
  line-height: 0;
`;

const Img = styled.img<{ pixelated: boolean }>`
  width: 100%;
  height: auto;
  display: block;
  image-rendering: ${({ pixelated }) => (pixelated ? 'pixelated' : 'auto')};
  ${({ pixelated }) =>
    pixelated &&
    `
    image-rendering: -moz-crisp-edges;
    image-rendering: crisp-edges;
  `}
`;

const StageTag = styled.div`
  font-family: var(--font-mono);
  font-size: 0.62rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--accent);
`;

const ShapeLabel = styled.div`
  font-family: var(--font-mono);
  font-size: 0.72rem;
  color: var(--text);
  letter-spacing: 0.02em;
`;

const ChannelLabel = styled.div`
  font-family: var(--font-mono);
  font-size: 0.62rem;
  color: var(--text-muted);
  letter-spacing: 0.02em;
`;

const FrameLabel = styled.div`
  font-family: var(--font-mono);
  font-size: 0.62rem;
  letter-spacing: 0.02em;
  color: var(--text-muted);
`;

const Divider = styled.div`
  height: 1px;
  background: var(--border);
  margin: 1.4rem 0 1.1rem;
`;

const Caption = styled.div`
  font-family: var(--font-body);
  font-size: 0.85rem;
  color: var(--text-muted);
  line-height: 1.6;
  margin-top: 1.25rem;
`;

const fadeIn = {
  hidden: { opacity: 0, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, delay: i * 0.06, ease: 'easeOut' as const },
  }),
};

const renderStage = (
  layer: LayerSpec,
  i: number,
  opts: { pixelated?: boolean } = {},
) => {
  const pixelated = opts.pixelated ?? layer.index <= 3;
  return (
    <CardMotion
      key={`layer-${layer.index}`}
      custom={i}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-40px' }}
      variants={fadeIn}
    >
      <StageTag>Stage {layer.index}</StageTag>
      <ImgFrame>
        <Img
          src={`${DATA}/layer-${layer.index}.png?v=4`}
          alt={`Activation map at PixelShuffle stage ${layer.index} (${layer.w}x${layer.h}, channel mean)`}
          pixelated={pixelated}
          draggable={false}
        />
      </ImgFrame>
      <ShapeLabel>{layer.w} × {layer.h}</ShapeLabel>
      <ChannelLabel>{layer.channels} channels</ChannelLabel>
    </CardMotion>
  );
};

const LayerReveal: React.FC = () => (
  <Wrapper>
    <Heading>Stages 0–5 · low-resolution feature maps · sample pair {SAMPLE_PAIR}</Heading>

    <StagesGrid>
      {STAGES_06.map((layer, i) => renderStage(layer, i))}
    </StagesGrid>

    <Divider />

    <Heading>Stage 6 · final pre-RGB feature map (384 × 512)</Heading>

    <FullWidthGrid>
      {renderStage(FINAL_STAGE, STAGES_06.length, { pixelated: false })}
    </FullWidthGrid>

    <Divider />

    <Heading>Decoder output · 384 × 512 RGB pair</Heading>

    <PairGrid>
      {[
        { idx: 0, label: 'Frame 0 (PoseNet-only)' },
        { idx: 1, label: 'Frame 1 (SegNet-graded)' },
      ].map((f, i) => (
        <CardMotion
          key={`frame-${f.idx}`}
          custom={STAGES_06.length + 1 + i}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-40px' }}
          variants={fadeIn}
        >
          <StageTag>Output</StageTag>
          <ImgFrame>
            <Img
              src={`${DATA}/frame-${f.idx}.png?v=4`}
              alt={f.label}
              pixelated={false}
              draggable={false}
            />
          </ImgFrame>
          <ShapeLabel>512 × 384</ShapeLabel>
          <FrameLabel>{f.label}</FrameLabel>
        </CardMotion>
      ))}
    </PairGrid>

    <Caption>
      A 28-d latent goes through a stem Linear (producing the 6×8 feature map in the
      top-left), then six PixelShuffle stages each doubling spatial resolution while
      tapering channel count, then a small refine + two RGB heads. The first few
      stages are coarse colour activity at very low resolution; recognisable road
      structure only crystallises around stage 4. The final feature map already
      contains the road geometry; the RGB heads turn it into pixels.
    </Caption>
  </Wrapper>
);

export default LayerReveal;
